import { useEffect, useRef, useState, useCallback } from "react";
import { useAppStore } from "../store/useAppStore";
import { api } from "../lib/api";
import { RotateCcw, ZoomIn, ZoomOut, Move, Loader2 } from "lucide-react";

export function EditorPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<any>(null);
  const [fabricLoaded, setFabricLoaded] = useState(false);

  const { currentDesign, selectedTemplate, editorState, setEditorState, setIsGenerating } =
    useAppStore();

  // Load Fabric.js dynamically
  useEffect(() => {
    let canvas: any;

    const initFabric = async () => {
      const { Canvas, Image: FabricImage } = await import("fabric");

      if (!canvasRef.current) return;

      canvas = new Canvas(canvasRef.current, {
        width: 600,
        height: 600,
        backgroundColor: "#f3f4f6",
      });

      fabricCanvasRef.current = canvas;
      setFabricLoaded(true);
    };

    initFabric();

    return () => {
      if (canvas) {
        canvas.dispose();
      }
    };
  }, []);

  // Load template and design when available
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !fabricLoaded) return;

    const loadImages = async () => {
      canvas.clear();

      if (selectedTemplate) {
        const { Image: FabricImage } = await import("fabric");
        const templateImg = await FabricImage.fromURL(selectedTemplate.templateUrl);
        templateImg.set({ selectable: false, evented: false });
        canvas.add(templateImg);
      }

      if (currentDesign) {
        const { Image: FabricImage } = await import("fabric");
        const designImg = await FabricImage.fromURL(currentDesign.originalUrl);
        designImg.set({
          left: 300 + editorState.posX,
          top: 300 + editorState.posY,
          scaleX: editorState.scale,
          scaleY: editorState.scale,
          angle: editorState.rotation,
          centeredScaling: true,
          centeredRotation: true,
        });
        canvas.add(designImg);
        canvas.setActiveObject(designImg);
      }

      canvas.renderAll();
    };

    loadImages();
  }, [currentDesign, selectedTemplate, fabricLoaded]);

  const handleObjectModified = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject) return;

    setEditorState({
      posX: activeObject.left - 300,
      posY: activeObject.top - 300,
      scale: activeObject.scaleX,
      rotation: activeObject.angle,
    });
  }, [setEditorState]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    canvas.on("object:modified", handleObjectModified);
    return () => {
      canvas.off("object:modified", handleObjectModified);
    };
  }, [handleObjectModified]);

  const handleGenerate = async () => {
    if (!currentDesign || !selectedTemplate) return;

    setIsGenerating(true);
    try {
      const result = await api.createMockup({
        designId: currentDesign.id,
        templateId: selectedTemplate.id,
        posX: editorState.posX,
        posY: editorState.posY,
        scale: editorState.scale,
        rotation: editorState.rotation,
      });

      // Poll for completion
      const poll = setInterval(async () => {
        const mockup = await api.getMockup(result.id);
        if (mockup.status === "COMPLETED") {
          clearInterval(poll);
          useAppStore.getState().addMockup(mockup);
          setIsGenerating(false);
        } else if (mockup.status === "FAILED") {
          clearInterval(poll);
          setIsGenerating(false);
          alert("Mockup generation failed");
        }
      }, 2000);
    } catch (error) {
      setIsGenerating(false);
      console.error(error);
    }
  };

  if (!currentDesign) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">請先上傳設計圖</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Canvas */}
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <canvas ref={canvasRef} className="mx-auto border border-gray-100 rounded-lg" />
          {!fabricLoaded && (
            <div className="text-center py-20">
              <Loader2 className="animate-spin h-8 w-8 mx-auto text-primary-500" />
              <p className="mt-2 text-gray-500">加載編輯器中...</p>
            </div>
          )}
        </div>

        {selectedTemplate && (
          <div className="text-center text-sm text-gray-500">
            產品: {selectedTemplate.name} · 打印區域: {selectedTemplate.printArea.width}×{selectedTemplate.printArea.height}px
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Move className="h-4 w-4" />
            設計調整
          </h3>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-600">位置 X</label>
              <input
                type="number"
                value={Math.round(editorState.posX)}
                onChange={(e) => setEditorState({ posX: Number(e.target.value) })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">位置 Y</label>
              <input
                type="number"
                value={Math.round(editorState.posY)}
                onChange={(e) => setEditorState({ posY: Number(e.target.value) })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">縮放</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditorState({ scale: Math.max(0.1, editorState.scale - 0.1) })}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50"
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <span className="w-16 text-center font-mono">{editorState.scale.toFixed(2)}x</span>
                <button
                  onClick={() => setEditorState({ scale: editorState.scale + 0.1 })}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50"
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-600">旋轉</label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="-180"
                  max="180"
                  value={editorState.rotation}
                  onChange={(e) => setEditorState({ rotation: Number(e.target.value) })}
                  className="flex-1"
                />
                <span className="w-12 text-right font-mono">{Math.round(editorState.rotation)}°</span>
              </div>
            </div>

            <button
              onClick={() => setEditorState({ posX: 0, posY: 0, scale: 1, rotation: 0 })}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <RotateCcw className="h-4 w-4" />
              重置
            </button>
          </div>
        </div>

        <button
          onClick={handleGenerate}
          disabled={!selectedTemplate || useAppStore.getState().isGenerating}
          className="w-full py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {useAppStore.getState().isGenerating ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="animate-spin h-5 w-5" />
              生成中...
            </span>
          ) : (
            "生成 Mockup"
          )}
        </button>
      </div>
    </div>
  );
}
